import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import DescriptionIcon from "@mui/icons-material/Description";
import HandshakeIcon from "@mui/icons-material/Handshake";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import BusinessIcon from "@mui/icons-material/Business";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import EventIcon from "@mui/icons-material/Event";
import { getInbox, markAsRead } from "../../services/inbox.service";
import { Notification } from "../../validation/customer.validation";
import { useUser } from "../../context/UserContext";
import {
  NOTIFICATION_COLORS,
  NOTIFICATION_COPY,
  NOTIFICATION_LABELS,
  NOTIFICATION_ROLE_TYPE_MAP,
  NOTIFICATION_ROW_OPTIONS,
  NOTIFICATION_STATUS_OPTIONS,
} from "../../constants/notification.constant";

const notificationIcons: Record<string, React.ReactNode> = {
  document_request: <DescriptionIcon />,
  document_reminder: <DescriptionIcon />,
  document_uploaded: <DescriptionIcon />,
  document_updated: <DescriptionIcon />,
  document_accepted: <AssignmentTurnedInIcon />,
  document_rejected: <DescriptionIcon />,
  deal_status_update: <HandshakeIcon />,
  banker_assigned: <AccountBalanceIcon />,
  deal_assigned: <HandshakeIcon />,
  term_sheet_received: <AssignmentTurnedInIcon />,
  closing_reminder: <EventIcon />,
  customer_details_shared: <MarkEmailReadIcon />,
  lead_created: <PersonAddIcon />,
  lead_invited: <PersonAddIcon />,
  lead_registered: <PersonAddIcon />,
  customer_registered: <PersonAddIcon />,
  deal_created: <HandshakeIcon />,
  banker_invited: <AccountBalanceIcon />,
  banker_registered: <AccountBalanceIcon />,
  business_registered: <BusinessIcon />,
  demo_request: <MarkEmailReadIcon />,
  contact_submission: <MarkEmailReadIcon />,
  waitlist_signup: <MarkEmailReadIcon />,
  bid_response: <HandshakeIcon />,
};

const allTypeOptions = Object.entries(NOTIFICATION_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const Notifications: React.FC = () => {
  const { user } = useUser();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"all" | "unread" | "read">("all");
  const [type, setType] = useState("all");
  const [paginationData, setPaginationData] = useState({
    page: 1,
    limit: NOTIFICATION_ROW_OPTIONS[0],
    total: 0,
  });

  const totalPages = Math.max(1, Math.ceil(paginationData.total / paginationData.limit));
  const unreadCount = notifications.filter((notif) => !notif.isRead).length;
  const typeOptions = useMemo(() => {
    const allowedTypes = NOTIFICATION_ROLE_TYPE_MAP[user?.role || ""] || Object.keys(NOTIFICATION_LABELS);
    return allTypeOptions.filter((option) => allowedTypes.includes(option.value));
  }, [user?.role]);

  const activeFilterCount = useMemo(
    () => [search.trim(), status !== "all", type !== "all"].filter(Boolean).length,
    [search, status, type],
  );

  const fetchNotifications = async (page = paginationData.page) => {
    setLoading(true);
    setError("");
    try {
      const res = await getInbox({
        page,
        limit: paginationData.limit,
        search,
        status,
        type,
      });
      setNotifications(res?.data || []);
      setPaginationData({
        page: res?.page || page,
        limit: res?.limit || paginationData.limit,
        total: res?.total || 0,
      });
    } catch (err: any) {
      setError(err?.message || NOTIFICATION_COPY.loadError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      fetchNotifications(1);
    }, 250);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, type, paginationData.limit]);

  useEffect(() => {
    if (type !== "all" && !typeOptions.some((option) => option.value === type)) {
      setType("all");
    }
  }, [type, typeOptions]);

  const handleMarkAsRead = async (id: string) => {
    try {
      await markAsRead(id);
      setNotifications((prev) =>
        prev.map((notif) => (notif.id === id ? { ...notif, isRead: true } : notif)),
      );
    } catch (err: any) {
      setError(err?.message || NOTIFICATION_COPY.markReadError);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setStatus("all");
    setType("all");
  };

  const formatDate = (date: string) =>
    new Date(date).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });

  return (
    <Box sx={{ width: "100%", p: { xs: 2, md: 3 }, bgcolor: NOTIFICATION_COLORS.pageBackground, minHeight: "100%" }}>
      <Stack spacing={2.5}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.5 },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Badge badgeContent={unreadCount} color="error">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 1.5,
                    bgcolor: NOTIFICATION_COLORS.brand,
                    color: NOTIFICATION_COLORS.white,
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <NotificationsIcon />
                </Box>
              </Badge>
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  {NOTIFICATION_COPY.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {NOTIFICATION_COPY.subtitle}
                </Typography>
              </Box>
            </Stack>
            <Chip
              label={`${paginationData.total} ${NOTIFICATION_COPY.totalSuffix}`}
              sx={{ bgcolor: NOTIFICATION_COLORS.brandLight, color: NOTIFICATION_COLORS.brand, fontWeight: 700 }}
            />
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
            <TextField
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={NOTIFICATION_COPY.searchPlaceholder}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 170 } }}>
              <InputLabel>{NOTIFICATION_COPY.statusLabel}</InputLabel>
              <Select label={NOTIFICATION_COPY.statusLabel} value={status} onChange={(event) => setStatus(event.target.value as any)}>
                {NOTIFICATION_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 220 } }}>
              <InputLabel>{NOTIFICATION_COPY.typeLabel}</InputLabel>
              <Select label={NOTIFICATION_COPY.typeLabel} value={type} onChange={(event) => setType(event.target.value)}>
                <MenuItem value="all">{NOTIFICATION_COPY.allTypesLabel}</MenuItem>
                {typeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 120 } }}>
              <InputLabel>{NOTIFICATION_COPY.rowsLabel}</InputLabel>
              <Select
                label={NOTIFICATION_COPY.rowsLabel}
                value={paginationData.limit}
                onChange={(event) =>
                  setPaginationData((prev) => ({ ...prev, limit: Number(event.target.value), page: 1 }))
                }
              >
                {NOTIFICATION_ROW_OPTIONS.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {activeFilterCount > 0 && (
              <Button variant="outlined" onClick={clearFilters} sx={{ whiteSpace: "nowrap" }}>
                {NOTIFICATION_COPY.clearLabel}
              </Button>
            )}
          </Stack>
        </Paper>

        {error && <Alert severity="error">{error}</Alert>}

        <Paper
          elevation={0}
          sx={{
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          {loading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {NOTIFICATION_COPY.loading}
              </Typography>
            </Stack>
          ) : notifications.length === 0 ? (
            <Stack alignItems="center" textAlign="center" sx={{ py: 8, px: 2 }}>
              <NotificationsIcon sx={{ fontSize: 56, color: "grey.400", mb: 1 }} />
              <Typography variant="h6">{NOTIFICATION_COPY.emptyTitle}</Typography>
              <Typography variant="body2" color="text.secondary">
                {NOTIFICATION_COPY.emptyDescription}
              </Typography>
            </Stack>
          ) : (
            <Stack divider={<Box sx={{ borderTop: "1px solid", borderColor: "divider" }} />}>
              {notifications.map((notification) => {
                const label = NOTIFICATION_LABELS[notification.type] || notification.type.replace(/_/g, " ");
                return (
                  <Box
                    key={notification.id}
                    sx={{
                      p: { xs: 1.5, md: 2 },
                      bgcolor: notification.isRead ? NOTIFICATION_COLORS.white : NOTIFICATION_COLORS.unreadBackground,
                      borderLeft: "4px solid",
                      borderLeftColor: notification.isRead ? NOTIFICATION_COLORS.transparent : NOTIFICATION_COLORS.brand,
                    }}
                  >
                    <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1.5,
                          bgcolor: notification.isRead ? NOTIFICATION_COLORS.readIconBackground : NOTIFICATION_COLORS.unreadIconBackground,
                          color: NOTIFICATION_COLORS.brand,
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                        }}
                      >
                        {notificationIcons[notification.type] || <NotificationsIcon />}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                          <Chip label={label} size="small" sx={{ height: 24, fontWeight: 700 }} />
                          {!notification.isRead && <Chip label={NOTIFICATION_COPY.newLabel} color="primary" size="small" variant="outlined" />}
                        </Stack>
                        <Typography sx={{ mt: 1, fontWeight: notification.isRead ? 500 : 700 }}>
                          {notification.message}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.75 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(notification.createdAt)}
                          </Typography>
                          {notification.deal?.loanName && (
                            <Typography variant="caption" color="text.secondary">
                              {NOTIFICATION_COPY.dealLabel}: {notification.deal.loanName}
                            </Typography>
                          )}
                          {(notification.banker as any)?.firstName && (
                            <Typography variant="caption" color="text.secondary">
                              {NOTIFICATION_COPY.bankerLabel}:{" "}
                              {`${(notification.banker as any).firstName || ""} ${(notification.banker as any).lastName || ""}`.trim()}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                      {!notification.isRead && (
                        <Tooltip title={NOTIFICATION_COPY.markAsRead}>
                          <IconButton color="primary" size="small" onClick={() => handleMarkAsRead(notification.id)}>
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Paper>

        {paginationData.total > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems="center"
              justifyContent="space-between"
              spacing={1.5}
            >
              <Typography variant="body2" color="text.secondary">
                {NOTIFICATION_COPY.showingLabel} {(paginationData.page - 1) * paginationData.limit + 1}{" "}
                {NOTIFICATION_COPY.toLabel}{" "}
                {Math.min(paginationData.page * paginationData.limit, paginationData.total)} {NOTIFICATION_COPY.ofLabel}{" "}
                {paginationData.total}
              </Typography>
              <Pagination
                count={totalPages}
                page={paginationData.page}
                onChange={(_, value) => fetchNotifications(value)}
                color="primary"
                shape="rounded"
              />
            </Stack>
          </Paper>
        )}
      </Stack>
    </Box>
  );
};

export default Notifications;
